import { ITime } from '../models/time';

export abstract class StringHelper {

    /**
     * Append @param {char} on the left side,
     * until the length of the string reaches @param {size}
     *
     * @static
     * @param {string} str
     * @param {number} size
     * @param {string} [char='0']
     * @returns {string}
     * @memberof StringHelper
     */
    static padLeft(str: string, size: number, char: string = '0'): string {
        let s = str;
        while (s.length < size) {
            s = char + s;
        }
        return s;
    }

    /**
     * Append @param {char} on the right side,
     * until the length of the string reaches @param {size}
     *
     * @static
     * @param {string} str
     * @param {number} size
     * @param {string} [char='0']
     * @returns {string}
     * @memberof StringHelper
     */
    static padRight(str: string, size: number, char: string = '0'): string {
        let s = str;
        while (s.length < size) {
            s += char;
        }
        return s;
    }

    /**
     * create a pseudo-random GUID
     *
     * @static
     * @returns
     * @memberof StringHelper
     */
    static getGuid(): string {
        return [
            StringHelper.getRandom(2),
            StringHelper.getRandom(),
            StringHelper.getRandom(),
            StringHelper.getRandom(),
            StringHelper.getRandom(3)
        ].join('-');
    }

    static getRandom(blocks: number = 1): string {
        let data = '';
        for (let i = 0; i < blocks; i++) {
            data += Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        }
        return data;
    }

    /**
     * format timespan (eg. 13:00 -  17:45)
     *
     * @param {ITimeSpan} span
     * @returns
     * @memberof RoomAllocationComponent
     */
    static formatTimeSpan(fromHour: number, fromMin: number, toHour: number, toMin: number, separator: string = ' - '): string {
        return StringHelper.formatTime(fromHour, fromMin) + separator + StringHelper.formatTime(toHour, toMin);
    }

    /**
     * format time (eg. 15:00)
     *
     * @param {number} hour
     * @param {number} min
     * @returns
     * @memberof RoomAllocationComponent
     */
    static formatTime(hour: number, min: number = 0): string {
        return StringHelper.padLeft(String(hour), 2, '0') + ':' + StringHelper.padLeft(String(min), 2, '0');
    }

    /**
     * format time from given minutes (eg. 15:00)
     *
     * @static
     * @param {number} minutes
     * @returns {string}
     * @memberof StringHelper
     */
    static formatTimeMinutes(minutes: number): string {
        return StringHelper.padLeft(String(Math.floor(minutes / 60)), 2, '0') + ':' + StringHelper.padLeft(String(minutes % 60), 2, '0');
    }

    /**
     * Returns true if the string is null or empty string
     *
     * @static
     * @param {string} s
     * @returns {boolean}
     * @memberof StringHelper
     */
    static isNullOrEmpty(s: string): boolean {
        return s === null || s.length === 0;
    }

    /**
     * Returns true if the string is not null and not empty string
     *
     * @static
     * @param {string} s
     * @returns {boolean}
     * @memberof StringHelper
     */
    static hasAny(s: string): boolean {
        return !StringHelper.isNullOrEmpty(s);
    }

    /**
     * Returns empty-string if value is null
     *
     * @static
     * @param {string} s
     * @returns {string}
     * @memberof StringHelper
     */
    static stringOrEmpty(s: string): string {
        return s === null ? '' : s;
    }

    /**
     * Returns null if string is empty
     *
     * @static
     * @param {string} s
     * @returns {(string | null)}
     * @memberof StringHelper
     */
    static nullIfEmpty(s: string): string | null {
        return s === '' ? null : s;
    }

    /**
     * Parse given token into object
     *
     * @static
     * @param {string} token
     * @returns {*}
     * @memberof StringHelper
     */
    public static parseToken(token: string): any {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    /**
     * Converts '\r\n' and '\n' to <br>
     *
     * @static
     * @param {string} s
     * @returns {string}
     * @memberof StringHelper
     */
    public static nl2br(s: string): string {
        return s.replace(new RegExp(/(\r\n)|(\n)/, 'g'), '<br>');
    }

    public static stringOrDefault(object: any, key: string, defaultValue: string = null): string {
        return object[key] === undefined ? defaultValue : object[key];
    }

    public static numberOrDefault(object: any, key: string, defaultValue: number = null): number {
        return object[key] === undefined ? defaultValue : Number(object[key]);
    }

    public static boolOrDefault(object: any, key: string, defaultValue: boolean = false): boolean {
        return object[key] === undefined ? defaultValue : object[key] === '1' || String(object[key]).toUpperCase() === 'TRUE';
    }

    public static dateOrDefault(object: any, key: string, defaultValue: Date = null): Date {
        if (object[key] === undefined || StringHelper.isNullOrEmpty(object[key])) {
            return defaultValue;
        }
        return new Date(object[key]);
    }

    public static timeOrDefault(object: any, key: string, defaultValue: ITime = null): ITime {
        if (object[key] === undefined || StringHelper.isNullOrEmpty(object[key])) {
            return defaultValue;
        }

        const parts = object[key].split(':');
        if (parts.length > 1) {
            return { hour: Number(parts[0]), minute: Number(parts[1]), second: parts.length === 3 ? Number(parent[2]) : 0 };
        }

        return defaultValue;
    }

    public static stringArrOrDefault(object: any, key: string, defaultValue: string[] = null): string[] {
        return object[key] === undefined ? defaultValue : object[key].split(/[,;]/);
    }

    public static numberArrOrDefault(object: any, key: string, defaultValue: number[] = null): number[] {
        return object[key] === undefined ? defaultValue : object[key].split(/[,;]/).map(Number);
    }

    public static replaceSection(text: string, replacementText: string, start: number, end: number) {
        return text.slice(0, Math.max(start, 0)) + replacementText + text.slice(end);
    }

    /**
     * Encodes a string as base64url without padding
     *
     * @param {string} binaryString
     * @returns {string}
     */
    public static base64UrlEncodeString(binaryString: string): string {
        return this.convertBase64ToBase64Url(btoa(binaryString));
    }

    /**
    * Encodes a Uint8Array as base64url without padding
    *
    * @param {Uint8Array} bytes
    * @returns {string}
    */
    public static base64UrlEncodeBytes(bytes: Uint8Array): string {
        let binaryString = "";
        for (var i = 0; i < bytes.length; i++) {
            binaryString += String.fromCodePoint(bytes[i]);
        }
        return this.base64UrlEncodeString(binaryString);
    }

    /**
     * Converts a Base64 encoded string to a Base64Url encoded string without padding
     *
     * @param {string} base64
     * @returns {string}
     */
    public static convertBase64ToBase64Url(base64: string): string {
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/[=]+$/, '');
    }

}
